import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";

const years = [
  1990,
  1992,
  1994,
  1996,
  1998,
  2000,
  2002,
  2004,
  2006,
  2008,
  2010,
  2012,
  2014,
  2016,
  2018,
  "2020/2021",
  2024,
  2026,
];

function getEventData(t, year) {
  const raw = t(`chinwooWorldChampionships.events.${year}`, {
    returnObjects: true,
  });
  if (
    raw &&
    typeof raw === "object" &&
    typeof raw.country === "string" &&
    typeof raw.location === "string" &&
    typeof raw.flag === "string"
  ) {
    return raw;
  }
  return {
    country: String(year),
    location: "—",
    flag: `${process.env.PUBLIC_URL || ""}/images/placeholder.jpg`,
  };
}

function ChinWooWorldChampionships() {
  const { t, i18n } = useTranslation();

  const resolvedYears = useMemo(
    () =>
      years.map((year, index) => ({
        year,
        index,
        edition: index + 1,
        event: getEventData(t, year),
      })),
    [t, i18n.language]
  );

  return (
    <section
      className="relative z-10 bg-slate-50/50 pb-14 pt-12 transition-colors dark:bg-slate-900 md:pb-16 md:pt-14"
      id="history"
      aria-labelledby="chinwoo-world-championships-heading"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <header className="mb-10 text-center md:mb-12">
          <h2
            id="chinwoo-world-championships-heading"
            className="relative mb-5 inline-block text-4xl font-semibold tracking-tight text-primary dark:text-primary-light md:text-5xl"
          >
            {t("chinwooWorldChampionships.title")}
            <span
              className="absolute -bottom-2.5 left-1/2 h-0.5 w-16 -translate-x-1/2 rounded-full bg-primary dark:bg-primary-light"
              aria-hidden
            />
          </h2>
          <p className="mx-auto mt-5 max-w-3xl text-lg leading-relaxed text-slate-600 dark:text-slate-400">
            {t("chinwooWorldChampionships.description")}
          </p>
        </header>

        <div className="relative mx-auto max-w-6xl">
          <div
            className="pointer-events-none absolute left-1/2 top-0 hidden h-full w-px -translate-x-1/2 bg-primary/35 dark:bg-primary-light/35 md:block"
            aria-hidden
          />

          <ol
            className="m-0 list-none space-y-10 p-0 md:space-y-14"
            aria-label={t("chinwooWorldChampionships.timeline_label")}
          >
            {resolvedYears.map(({ year, index, edition, event }) => {
              const isEven = index % 2 === 0;
              const eventData = event;

              /* Shell wie ScheduleTailwind; Jahr als Watermark; Ausgabe nur sr-only */
              const cardInner = (
                <article
                  className={`group relative w-full max-w-lg overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition-all dark:border-slate-700 dark:bg-slate-800 motion-safe:hover:shadow-sm ${
                    isEven
                      ? "border-l-4 border-l-slate-300 dark:border-l-slate-600"
                      : "border-r-4 border-r-slate-300 dark:border-r-slate-600"
                  }`}
                  aria-labelledby={`cwc-year-${year}`}
                >
                  <span
                    className={`pointer-events-none absolute top-2 max-w-[48%] select-none break-words text-4xl font-black tabular-nums leading-[0.95] text-primary/[0.09] dark:text-primary-light/[0.11] sm:top-2.5 sm:text-5xl md:top-3 md:text-6xl ${
                      isEven
                        ? "right-6 text-right sm:right-10 md:right-14"
                        : "left-6 text-left sm:left-10 md:left-14"
                    }`}
                    aria-hidden
                  >
                    {year}
                  </span>
                  <p id={`cwc-year-${year}`} className="sr-only">
                    {year}
                    {", "}
                    {t("chinwooWorldChampionships.edition_sr", { n: edition })}
                  </p>
                  <div
                    className={`relative z-[1] px-5 py-4 sm:px-6 sm:py-5 ${
                      isEven
                        ? "pr-[46%] sm:pr-[44%] md:pr-52"
                        : "pl-[46%] sm:pl-[44%] md:pl-52"
                    }`}
                  >
                    <div
                      className={`flex items-start gap-4 ${
                        isEven ? "flex-row" : "flex-row-reverse"
                      }`}
                    >
                      <img
                        src={eventData.flag}
                        alt={t("chinwooWorldChampionships.flag_alt", {
                          country: eventData.country,
                        })}
                        width={52}
                        height={52}
                        className="h-12 w-12 shrink-0 rounded-full border-2 border-slate-200 object-cover shadow-sm dark:border-slate-600 sm:h-14 sm:w-14"
                      />
                      <div
                        className={`min-w-0 flex-1 ${
                          isEven ? "text-left" : "text-right"
                        }`}
                      >
                        <h3 className="break-words text-base font-semibold leading-snug text-slate-900 dark:text-slate-100">
                          {eventData.country}
                        </h3>
                        <div
                          className={`mt-1.5 flex flex-wrap items-center gap-1.5 text-sm leading-snug text-slate-500 dark:text-slate-400 ${
                            isEven ? "justify-start" : "w-full justify-end"
                          }`}
                        >
                          <span
                            className={`inline-flex max-w-full items-start gap-1.5 ${
                              isEven ? "flex-row" : "flex-row-reverse"
                            }`}
                          >
                            <span
                              className="flex h-[1.25em] shrink-0 items-center"
                              aria-hidden
                            >
                              <i className="fas fa-map-marker-alt text-[0.65em] leading-none" />
                            </span>
                            <span className="min-w-0 break-words">
                              {eventData.location}
                            </span>
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </article>
              );

              return (
                <li
                  key={year}
                  className="relative grid grid-cols-1 gap-4 md:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] md:items-center md:gap-6"
                >
                  {isEven ? (
                    <>
                      <div className="md:col-start-1 md:flex md:justify-end md:pr-6">
                        {cardInner}
                      </div>
                      <div
                        className="relative hidden h-3 w-3 flex-shrink-0 rounded-full border-2 border-white bg-primary shadow-sm dark:border-slate-800 dark:bg-primary-light md:col-start-2 md:flex md:items-center md:justify-center"
                        aria-hidden
                      />
                      <div
                        className="hidden min-h-0 md:col-start-3 md:block"
                        aria-hidden
                      />
                    </>
                  ) : (
                    <>
                      <div
                        className="hidden min-h-0 md:col-start-1 md:block"
                        aria-hidden
                      />
                      <div
                        className="relative hidden h-3 w-3 flex-shrink-0 rounded-full border-2 border-white bg-primary shadow-sm dark:border-slate-800 dark:bg-primary-light md:col-start-2 md:flex md:items-center md:justify-center"
                        aria-hidden
                      />
                      <div className="md:col-start-3 md:flex md:justify-start md:pl-6">
                        {cardInner}
                      </div>
                    </>
                  )}
                </li>
              );
            })}
          </ol>
        </div>
      </div>
    </section>
  );
}

export default ChinWooWorldChampionships;
